/**
 * USDC read layer (SEP 5.b.1 §H-K).
 *
 * Minimal READ-ONLY ERC20 ABI + a pure gate that decides whether the
 * balanceOf/allowance reads may be issued. The gate is exported so it
 * can be unit-tested WITHOUT actually hitting the RPC.
 *
 * ABI intentionally omits approve/transfer/transferFrom — 5.b.1 is
 * strictly read-only. Write methods will be added in 5.c, guarded by
 * the exact-approval policy already ratified.
 */
import { useReadContract } from "wagmi";
import type { Abi } from "viem";
import type { SaleConfig, SaleStatus } from "./sale-types";
import type { WalletPreflightState } from "./wallet-preflight";

// Minimal read-only ERC20 ABI — balanceOf + allowance only.
export const ERC20_READ_ABI = [
  {
    type: "function",
    name: "balanceOf",
    stateMutability: "view",
    inputs: [{ name: "account", type: "address" }],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    type: "function",
    name: "allowance",
    stateMutability: "view",
    inputs: [
      { name: "owner", type: "address" },
      { name: "spender", type: "address" },
    ],
    outputs: [{ name: "", type: "uint256" }],
  },
] as const satisfies Abi;

// ---------------------------------------------------------------------
// Read-gate — pure predicate. Read hooks only fire when this is true.
// ---------------------------------------------------------------------
export interface FinancialReadGateInputs {
  config: SaleConfig | null;
  status: SaleStatus | null;
  preflight: WalletPreflightState;
}

export type FinancialReadGate =
  | { allowed: true; ctx: FinancialReadContext }
  | { allowed: false; reason: FinancialReadBlocker };

export interface FinancialReadContext {
  chainId: number;
  owner: `0x${string}`;
  token: `0x${string}`;
  spender: `0x${string}`;
  decimals: number;
}

export type FinancialReadBlocker =
  | "config_not_ready"
  | "generation_not_v2"
  | "backend_not_operational"
  | "sale_paused"
  | "wallet_disconnected"
  | "wrong_chain"
  | "unsupported_chain";

/**
 * Pure evaluator. Consumers pass the CURRENT snapshot; hook rerenders
 * take care of re-evaluation. NO React inside.
 */
export function evaluateFinancialReadGate(
  i: FinancialReadGateInputs,
  supportedChainIds: readonly number[],
): FinancialReadGate {
  const { config, status, preflight } = i;
  if (config == null || status == null) {
    return { allowed: false, reason: "config_not_ready" };
  }
  if (config.generation !== "v2") {
    return { allowed: false, reason: "generation_not_v2" };
  }
  if (status.generation !== "v2") {
    return { allowed: false, reason: "generation_not_v2" };
  }
  if (status.state !== "ok") {
    return { allowed: false, reason: "backend_not_operational" };
  }
  if (status.paused) {
    return { allowed: false, reason: "sale_paused" };
  }
  if (preflight.status === "no_provider" || preflight.status === "disconnected") {
    return { allowed: false, reason: "wallet_disconnected" };
  }
  if (preflight.status === "wrong_chain") {
    return { allowed: false, reason: "wrong_chain" };
  }
  // Unsupported chain: backend expects a chain outside our wagmi config.
  if (!supportedChainIds.includes(preflight.expectedChainId)) {
    return { allowed: false, reason: "unsupported_chain" };
  }
  if (!preflight.address) {
    return { allowed: false, reason: "wallet_disconnected" };
  }
  return {
    allowed: true,
    ctx: {
      chainId: config.chain_id,
      owner: preflight.address,
      token: config.payment_token as `0x${string}`,
      spender: config.sale_contract_address as `0x${string}`,
      decimals: config.payment_token_decimals,
    },
  };
}

// ---------------------------------------------------------------------
// Hooks — thin wagmi wrappers. Read only when `enabled` (from gate).
// ---------------------------------------------------------------------
export function useUsdcBalanceOf(
  ctx: FinancialReadContext | null,
): { data: bigint | undefined; isLoading: boolean; error: unknown; refetch: () => void } {
  const q = useReadContract({
    abi: ERC20_READ_ABI,
    address: ctx?.token,
    functionName: "balanceOf",
    args: ctx ? [ctx.owner] : undefined,
    chainId: ctx?.chainId,
    query: { enabled: ctx != null },
  });
  return {
    data: q.data as bigint | undefined,
    isLoading: q.isLoading,
    error: q.error,
    refetch: () => { void q.refetch(); },
  };
}

export function useUsdcAllowance(
  ctx: FinancialReadContext | null,
): { data: bigint | undefined; isLoading: boolean; error: unknown; refetch: () => void } {
  const q = useReadContract({
    abi: ERC20_READ_ABI,
    address: ctx?.token,
    functionName: "allowance",
    args: ctx ? [ctx.owner, ctx.spender] : undefined,
    chainId: ctx?.chainId,
    query: { enabled: ctx != null },
  });
  return {
    data: q.data as bigint | undefined,
    isLoading: q.isLoading,
    error: q.error,
    refetch: () => { void q.refetch(); },
  };
}
